import { defineStore } from "pinia";
import { ref } from "vue";
import { getSprint } from "@/api/sprints";
import type { ApiError, SprintDetail } from "@/api/types";
import { useApi } from "@/composables/useApi";

interface CacheEntry {
  data: SprintDetail | null;
  loadedAt: number;
  error: ApiError | null;
}

function cacheKey(projectKey: string, sprintId: number | string): string {
  return `${projectKey}::${sprintId}`;
}

export const useSprintDetailStore = defineStore("sprintDetail", () => {
  const byKey = ref<Record<string, CacheEntry>>({});
  const loadingKey = ref<string | null>(null);

  function entry(projectKey: string, sprintId: number | string): CacheEntry {
    return byKey.value[cacheKey(projectKey, sprintId)] ?? { data: null, loadedAt: 0, error: null };
  }

  async function load(projectKey: string, sprintId: number | string, force = false): Promise<void> {
    const k = cacheKey(projectKey, sprintId);
    const cur = byKey.value[k];
    if (!force && cur && cur.data && Date.now() - cur.loadedAt < 60_000) return;
    const { client } = useApi();
    loadingKey.value = k;
    try {
      const data = await getSprint(client, projectKey, sprintId);
      byKey.value = {
        ...byKey.value,
        [k]: { data, loadedAt: Date.now(), error: null },
      };
    } catch (err) {
      byKey.value = {
        ...byKey.value,
        [k]: { data: cur?.data ?? null, loadedAt: cur?.loadedAt ?? 0, error: err as ApiError },
      };
    } finally {
      if (loadingKey.value === k) loadingKey.value = null;
    }
  }

  function detail(projectKey: string, sprintId: number | string): SprintDetail | null {
    return entry(projectKey, sprintId).data;
  }
  function error(projectKey: string, sprintId: number | string): ApiError | null {
    return entry(projectKey, sprintId).error;
  }
  function loading(projectKey: string, sprintId: number | string): boolean {
    return loadingKey.value === cacheKey(projectKey, sprintId);
  }

  return { byKey, loadingKey, detail, error, loading, load };
});
